/**
 * ForcePasswordChangePage — Shown after a newly-invited user signs in
 * with their temporary password.
 *
 * Flow:
 *   1. Admin invites user → backend sends email with temp password
 *      and sets `force_password_change: true` on the user record.
 *   2. New user signs in with temp password → `/auth/me` returns
 *      `force_password_change: true`.
 *   3. The `ProtectedRoute` guard in App.js redirects every route
 *      except this one to `/force-password-change`.
 *   4. User submits temp password + new password. The existing
 *      `/auth/change-password` endpoint clears the flag.
 *   5. AuthContext refreshes → flag is gone → user lands on dashboard.
 *
 * No "current password" wording is used — the user thinks of the temp
 * password as "the one you emailed me", so the form labels follow that
 * mental model.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { setTokens } from '@/lib/authToken';
import { toast } from 'sonner';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, formatApiErrorDetail } from '@/contexts/AuthContext';
import { PasswordRules } from '@/components/auth/PasswordRules';
import { passwordRuleError } from '@/lib/passwordPolicy';

export default function ForcePasswordChangePage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [tempPassword, setTempPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const pwErr = passwordRuleError(newPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword === tempPassword) {
      setError('Choose a new password that is different from the temporary one.');
      return;
    }
    setBusy(true);
    try {
      const { data: cpData } = await apiClient.post('/auth/change-password', {
          current_password: tempPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        });
      setTokens(cpData);
      toast.success('Password updated. Welcome to Strata!');
      await refreshUser();
      // Came in through the crew clock QR → back to the clock, not the full app.
      let dest = null;
      try { dest = sessionStorage.getItem('post_login_redirect'); sessionStorage.removeItem('post_login_redirect'); } catch { /* ignore */ }
      navigate(dest === '/clock' ? '/clock' : '/', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Could not change your password.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-xl p-8 space-y-6" data-testid="force-password-change-page">
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-[#4575A8]/15 border border-[#4575A8]/30 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-[#4575A8]" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground" style={{ fontFamily: 'Outfit' }}>
            Set your password
          </h1>
          <p className="text-sm text-muted-foreground">
            Welcome to Strata{user?.name ? `, ${user.name}` : ''}. For your security, please replace the temporary password we emailed you with one of your own.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="temp-pw" className="text-xs uppercase tracking-widest text-muted-foreground">
              Temporary password
            </Label>
            <Input
              id="temp-pw"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={tempPassword}
              onChange={(e) => setTempPassword(e.target.value)}
              disabled={busy}
              required
              data-testid="force-temp-password-input"
              className="bg-background border-border text-foreground mt-1"
            />
          </div>

          <div>
            <Label htmlFor="new-pw" className="text-xs uppercase tracking-widest text-muted-foreground">
              New password
            </Label>
            <Input
              id="new-pw"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
              required
              data-testid="force-new-password-input"
              className="bg-background border-border text-foreground mt-1"
            />
            <PasswordRules password={newPassword} testId="force-password-rules" />
          </div>

          <div>
            <Label htmlFor="confirm-pw" className="text-xs uppercase tracking-widest text-muted-foreground">
              Confirm new password
            </Label>
            <Input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
              required
              data-testid="force-confirm-password-input"
              className="bg-background border-border text-foreground mt-1"
            />
          </div>

          {error && (
            <div className="text-xs text-[#AD5D5D] bg-[#AD5D5D]/10 border border-[#AD5D5D]/30 rounded p-2" data-testid="force-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy || !tempPassword || !newPassword || !confirmPassword}
            className="w-full bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
            data-testid="force-submit-btn"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" /> Set my password
              </>
            )}
          </Button>

          <button
            type="button"
            onClick={async () => { await logout(); navigate('/login', { replace: true }); }}
            className="block w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="force-signout-btn"
          >
            Sign out
          </button>
        </form>
      </div>
    </div>
  );
}
